import { axios } from '@/api/axios.ts';
import { axios2 } from '@/api/axiox2.ts';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import type {
  TSpendSummary,
  TStatusMaintenanceTask,
  TWindowMaintenanceTask,
  TMaintenanceTask,
  TMaintenanceTasksParams,
  TPaginatedMaintenanceTask,
  TMaintenanceTasksPaginatedResponse,
  TUpcomingPaginatedResponse,
} from './maintenance-tasks.types.ts';
import type {
  TRoomMaintenanceTask,
  TRoomMaintenanceTask2,
  TPaginatedGroupedTasksResponse,
} from '@/services/client-room-management/client-room-management.types.ts';
import type { TTAskPriority } from '@/services/tasks/tasks.types.ts';

export const maintenanceTasksApi = {
  getAllMaintenanceTasks: async (params: {
    priority?: TTAskPriority;
    year?: number;
    limit?: number;
    page?: number;
  }) => {
    const response = await axios.get<TRoomMaintenanceTask[]>('maintenance-tasks', { params });

    return response.data;
  },
  getAllMaintenanceTasks2: async () => {
    const response = await axios2.get<TRoomMaintenanceTask2[]>('maintenance-tasks');

    return response.data;
  },
  getMaintenanceTaskById: async (id: number) => {
    const response = await axios.get<TMaintenanceTask>(`maintenance-tasks/${id}`);

    return response.data;
  },
  getAllCompletedMaintenanceTasks: async () => {
    const response = await axios.get<TRoomMaintenanceTask[]>('maintenance-tasks/completed');

    return response.data;
  },
  getAllDelayedMaintenanceTasks: async () => {
    const response = await axios.get<TRoomMaintenanceTask[]>('maintenance-tasks/delayed');

    return response.data;
  },
  createCompleteMaintenanceTask: async (payload: { id: number; data?: any }) => {
    const { id, ...data } = payload;

    const response = await axios.post<TStatusMaintenanceTask>(
      `maintenance-tasks/${id}/complete`,
      data,
    );

    return response.data;
  },
  createDelayedMaintenanceTask: async (payload: { id: number; notes?: string }) => {
    const { id, notes } = payload;

    const response = await axios.post<TStatusMaintenanceTask>(`maintenance-tasks/${id}/delay`, {
      notes,
    });

    return response.data;
  },
  getMaintenanceTasksWindow: async (params: { from: string; to: string }) => {
    const response = await axios.get<TWindowMaintenanceTask[]>('maintenance-tasks/window', {
      params,
    });

    return response.data;
  },
  getOverdueMaintenanceTasks: async () => {
    const response = await axios.get<TWindowMaintenanceTask[]>('maintenance-tasks/overdue');

    return response.data;
  },
  getSpendSummary: async (year?: number) => {
    const response = await axios.get<TSpendSummary>('maintenance-tasks/spend-summary', {
      params: year ? { year } : {},
    });

    return response.data;
  },
  getPaginatedMaintenanceTasks: async (params: TMaintenanceTasksParams = {}) => {
    const response = await axios.get<TMaintenanceTasksPaginatedResponse<TPaginatedMaintenanceTask>>(
      'maintenance-tasks',
      {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
        },
      },
    );

    return response.data;
  },
  getPaginatedGroupedTasks: async (params: TMaintenanceTasksParams = {}) => {
    const response = await axios2.get<TPaginatedGroupedTasksResponse>('maintenance-tasks', {
      params: {
        page: params.page || 1,
        limit: params.limit || 10,
      },
    });

    return response.data;
  },
  getUpcomingMaintenanceTasks: async (params: TMaintenanceTasksParams = {}) => {
    const response = await axios2.get<TUpcomingPaginatedResponse<TPaginatedMaintenanceTask>>(
      'maintenance-tasks/upcoming',
      {
        params: {
          page: params.page || 1,
          limit: params.limit || 10,
          // Cross a year boundary by default so late-December still shows January tasks
          year: params.year,
        },
      },
    );

    return response.data;
  },
};

export const maintenanceTasksQueries = createQueryKeys('maintenance-tasks', {
  getAllMaintenanceTasks: (params) => ({
    queryKey: ['list', params],
    queryFn: () => maintenanceTasksApi.getAllMaintenanceTasks(params),
  }),
  getAllMaintenanceTasks2: () => ({
    queryKey: ['list'],
    queryFn: () => maintenanceTasksApi.getAllMaintenanceTasks2(),
  }),
  getMaintenanceTaskById: (id: number) => ({
    queryKey: ['task', id],
    queryFn: () => maintenanceTasksApi.getMaintenanceTaskById(id),
  }),
  getAllCompletedMaintenanceTasks: () => ({
    queryKey: ['completed'],
    queryFn: () => maintenanceTasksApi.getAllCompletedMaintenanceTasks(),
  }),
  getAllDelayedMaintenanceTasks: () => ({
    queryKey: ['delayed'],
    queryFn: () => maintenanceTasksApi.getAllDelayedMaintenanceTasks(),
  }),
  getPaginatedMaintenanceTasks: (limit: number = 10) => ({
    queryKey: ['paginated', limit],
  }),
  getPaginatedGroupedTasks: (limit: number = 10) => ({
    queryKey: ['paginated-grouped', limit],
  }),
  getUpcomingMaintenanceTasks: (limit: number = 10) => ({
    queryKey: ['upcoming', limit],
  }),
  getMaintenanceTasksWindow: (params: { from: string; to: string }) => ({
    queryKey: ['window', params.from, params.to],
    queryFn: () => maintenanceTasksApi.getMaintenanceTasksWindow(params),
  }),
  getOverdueMaintenanceTasks: () => ({
    queryKey: ['overdue'],
    queryFn: () => maintenanceTasksApi.getOverdueMaintenanceTasks(),
  }),
  getSpendSummary: (year?: number) => ({
    queryKey: ['spend-summary', year ?? 'current'],
    queryFn: () => maintenanceTasksApi.getSpendSummary(year),
  }),
});
